import { FirestoreTimestampSchema } from "./firestore.js";
import { z } from "zod";
import { OrderRawSchema, ShippingInfoSchema } from "./order.js";
import { AccountSchema, JobStatusSchema } from "./customer.js";
import { CustomerFeedbackSnapshotSchema } from "./feedback.js";
const TicketStatusSchema = z.enum([
  "open",
  "chatbot_handling",
  "waiting_for_agent",
  "assigned",
  "in_progress",
  "on_hold",
  "resolved",
  "closed"
]);
const TicketPrioritySchema = z.enum(["low", "medium", "high", "urgent"]);
const TicketSourceSchema = z.enum(["chat", "email", "phone", "web"]);
const PersonSnapshotSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  image: z.string().url().optional(),
  type: z.enum(["agent", "customer", "bot"]),
  anonymous: z.boolean().nullish().describe("\uC775\uBA85 \uC5EC\uBD80"),
  storeId: z.string().nullish().describe("\uC0C1\uC810 ID")
});
const MessagePreviewSchema = z.object({
  id: z.string(),
  type: z.string(),
  text: z.string(),
  author: PersonSnapshotSchema,
  createdAt: FirestoreTimestampSchema
});
const TicketMemoSchema = z.object({
  id: z.string().describe("Firestore \uBB38\uC11C ID"),
  content: z.string(),
  createdAt: FirestoreTimestampSchema,
  createdBy: z.string().describe("\uC0AC\uC6A9\uC790 UID \uB610\uB294 \uC2DD\uBCC4\uC790"),
  isDeleted: z.boolean().optional().describe("\uC18C\uD504\uD2B8 \uC0AD\uC81C \uC0AC\uC6A9 \uC2DC"),
  author: z.object({
    id: z.string(),
    name: z.string()
  }).optional()
});
const CustomerSnapshotSchema = z.object({
  id: z.string().optional().nullable().describe("\uACE0\uAC1D ID"),
  name: z.string(),
  companyName: z.string().optional().nullable(),
  email: z.string().email().optional().nullable(),
  phone: z.string().optional().nullable(),
  address: z.object({
    address1: z.string().optional().nullable(),
    address2: z.string().optional().nullable(),
    zip: z.string().optional().nullable(),
    dong: z.string().optional().nullable(),
    sgg: z.string().optional().nullable(),
    si: z.string().optional().nullable()
  }).optional().nullable(),
  metadata: z.record(z.any()).optional(),
  membership: z.string().optional().nullable(),
  jobStatus: z.array(JobStatusSchema).optional().nullable(),
  account: AccountSchema.optional().nullable(),
  anonymous: z.boolean().nullish().describe("\uC775\uBA85 \uC5EC\uBD80"),
  storeId: z.string().nullish().describe("\uC0C1\uC810 ID")
});
const OrderSnapshotSchema = z.object({
  orderNo: z.string().describe("\uC8FC\uBB38 \uBC88\uD638"),
  title: z.string().describe("\uC8FC\uBB38 \uC81C\uBAA9").optional().nullable(),
  orderStatus: z.string(),
  jobStatus: z.string().optional().nullable(),
  orderDate: z.string().datetime({ message: "\uC720\uD6A8\uD55C \uB0A0\uC9DC/\uC2DC\uAC04 \uBB38\uC790\uC5F4\uC774\uC5B4\uC57C \uD569\uB2C8\uB2E4." }).optional().nullable().describe("\uC8FC\uBB38 \uC77C\uC790 (ISO 8601 \uD615\uC2DD)"),
  shippingInfo: ShippingInfoSchema.optional().nullable(),
  raw: OrderRawSchema.optional().nullable(),
  amount: z.number().describe("\uC8FC\uBB38 \uAE08\uC561").optional().nullable()
});
const ChatbotSummarySchema = z.object({
  summary: z.string().nullable().optional(),
  finalSummary: z.string().nullable().optional()
});
const BaseTicketMessageSchema = z.object({
  id: z.string(),
  author: PersonSnapshotSchema,
  createdAt: FirestoreTimestampSchema,
  ticketId: z.string(),
  metadata: z.record(z.string(), z.any()).optional().nullish(),
  status: z.enum(["delivered", "error", "seen", "sending", "sent"]).optional(),
  readBy: z.array(z.string()).optional(),
  generatedMessageId: z.string().optional()
}).describe("\uAE30\uBCF8 \uD2F0\uCF13 \uBA54\uC2DC\uC9C0 \uC2A4\uD0A4\uB9C8");
const TicketTextMessageSchema = BaseTicketMessageSchema.extend({
  type: z.literal("text"),
  text: z.string()
}).describe("\uD14D\uC2A4\uD2B8 \uBA54\uC2DC\uC9C0 \uC2A4\uD0A4\uB9C8");
const TicketFileMessageSchema = BaseTicketMessageSchema.extend({
  type: z.literal("file"),
  name: z.string(),
  uri: z.string().url(),
  size: z.number(),
  mimeType: z.string().optional()
}).describe("\uD30C\uC77C \uBA54\uC2DC\uC9C0 \uC2A4\uD0A4\uB9C8");
const TicketImageMessageSchema = BaseTicketMessageSchema.extend({
  type: z.literal("image"),
  uri: z.string().url()
}).describe("\uC774\uBBF8\uC9C0 \uBA54\uC2DC\uC9C0 \uC2A4\uD0A4\uB9C8");
const TicketInternalNoteMessageSchema = BaseTicketMessageSchema.extend({
  type: z.literal("internal_note"),
  text: z.string()
}).describe("\uB0B4\uBD80 \uB178\uD2B8 \uBA54\uC2DC\uC9C0 \uC2A4\uD0A4\uB9C8");
const TicketMessageUnionSchema = z.discriminatedUnion("type", [
  TicketTextMessageSchema,
  TicketFileMessageSchema,
  TicketImageMessageSchema,
  TicketInternalNoteMessageSchema
]);
const TicketSchema = z.object({
  id: z.string(),
  customer: CustomerSnapshotSchema,
  orderId: z.string().nullish(),
  order: OrderSnapshotSchema.optional(),
  title: z.string(),
  description: z.string(),
  status: TicketStatusSchema,
  priority: TicketPrioritySchema,
  source: TicketSourceSchema.optional().nullable(),
  customerFeedbackId: z.string().optional().describe("\uACE0\uAC1D \uD53C\uB4DC\uBC31 ID"),
  tags: z.array(z.string()).optional().nullable(),
  assignedTo: z.string().nullable().optional(),
  assignedBy: z.string().nullable().optional(),
  references: z.array(z.string()).optional(),
  chatbotSummary: ChatbotSummarySchema.optional().nullable(),
  chatbotDuration: z.number().optional(),
  createdAt: FirestoreTimestampSchema,
  updatedAt: FirestoreTimestampSchema,
  assignedAt: FirestoreTimestampSchema.optional().nullable().describe("\uB2F4\uB2F9\uC790 \uBC30\uC815"),
  resolvedAt: FirestoreTimestampSchema.optional().nullable().default(null),
  resolved: z.boolean().optional().nullable().default(false),
  closedAt: FirestoreTimestampSchema.optional(),
  handlingTime: z.number().optional().nullable().describe("\uCC98\uB9AC \uC2DC\uAC04 (\uCD08)"),
  lastMessage: MessagePreviewSchema.optional().nullable(),
  lastState: z.string().optional().nullable(),
  metadata: z.record(z.string(), z.any()).optional().nullable(),
  feedback: CustomerFeedbackSnapshotSchema.nullable(),
  hasFeedback: z.boolean().nullable().optional()
});
const CreateTicketDtoSchema = z.object({
  customer: CustomerSnapshotSchema,
  order: OrderSnapshotSchema.optional(),
  title: z.string(),
  description: z.string(),
  source: TicketSourceSchema.optional(),
  priority: TicketPrioritySchema,
  tags: z.array(z.string()).optional().nullable(),
  chatRoomId: z.string().optional(),
  orderId: z.string().optional()
});
const UpdateTicketDtoSchema = z.object({
  status: TicketStatusSchema.optional(),
  priority: TicketPrioritySchema.optional(),
  assignedTo: z.string().nullable().optional(),
  assignedBy: z.string().optional(),
  references: z.array(z.string()).optional(),
  chatbotSummary: z.object({
    summary: z.string().nullable().optional(),
    finalSummary: z.string().nullable().optional()
  }).optional(),
  chatbotDuration: z.number().optional(),
  tags: z.array(z.string()).optional(),
  metadata: z.record(z.string(), z.any()).nullish()
});
export {
  BaseTicketMessageSchema,
  CreateTicketDtoSchema,
  CustomerSnapshotSchema,
  MessagePreviewSchema,
  OrderSnapshotSchema,
  PersonSnapshotSchema,
  TicketFileMessageSchema,
  TicketImageMessageSchema,
  TicketInternalNoteMessageSchema,
  TicketMemoSchema,
  TicketMessageUnionSchema,
  TicketPrioritySchema,
  TicketSchema,
  TicketSourceSchema,
  TicketStatusSchema,
  TicketTextMessageSchema,
  UpdateTicketDtoSchema
};
//# sourceMappingURL=ticket.js.map